import React, { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { PieChart as PieChartIcon } from "lucide-react";

const DimensionDistribution = ({ distributionData, onDimensionClick }) => {
  const [hoveredDimension, setHoveredDimension] = useState(null);
  const [selectedPieChartDimension, setSelectedPieChartDimension] = useState(null);

  if (!distributionData || distributionData.length === 0) return null;

  const colors = ["#3b82f6", "#f97316", "#10b981"];

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 col-span-2">
      <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
        <PieChartIcon className="w-5 h-5 text-blue-600" />
        Dimension Distribution
      </h3>

      <div className="pie-chart-container">
        {/* Pie Chart */}
        <div className="pie-chart-wrapper">
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={distributionData}
                cx="50%"
                cy="50%"
                outerRadius={120}
                paddingAngle={5}
                dataKey="total"
                onClick={(data) => setSelectedPieChartDimension({ dimension: data.name })}
                onMouseEnter={(data) => setHoveredDimension(data.name)}
                onMouseLeave={() => setHoveredDimension(null)}
                style={{ cursor: "pointer" }}
              >
                {distributionData.map((entry, index) => {
                  const isHovered = hoveredDimension === entry.name;
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={colors[index % colors.length]}
                      opacity={isHovered ? 0.8 : 0.6}
                      style={{ transition: "all 0.3s ease", cursor: "pointer" }}
                    />
                  );
                })}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="custom-chart-tooltip bg-white rounded-lg p-3 shadow-lg border border-gray-200">
                        <p className="text-sm font-bold text-gray-900">{data.name}</p>
                        <p className="text-xs text-gray-600">
                          Total: {(data.total || 0).toLocaleString()}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Dimension Details List */}
        <div className="dimension-details-list mt-6">
          {distributionData.map((dim, idx) => {
            const color = colors[idx] || "#64748b";
            const passedCount = dim.passed || 0;
            const failedCount = dim.failed || 0;
            const totalCount = dim.total || 0;

            return (
              <div key={idx} className="dimension-detail-item mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="dimension-detail-header mb-3">
                  <div className="dimension-name flex items-center gap-2" style={{ color }}>
                    <span
                      className="dimension-color-indicator inline-block w-3 h-3 rounded-full"
                      style={{ backgroundColor: color }}
                    ></span>
                    <span className="font-bold text-gray-900">{dim.name}</span>
                  </div>
                </div>
                <div className="dimension-detail-stats space-y-2">
                  <div className="stat-row flex justify-between text-sm">
                    <span className="stat-label text-gray-600">Total Records:</span>
                    <span className="stat-value font-semibold text-gray-900">
                      {typeof totalCount === "number" ? totalCount.toLocaleString() : "0"}
                    </span>
                  </div>
                  <div className="stat-row flex justify-between text-sm success">
                    <span className="stat-label text-green-600">Passed:</span>
                    <span className="stat-value font-semibold text-green-600">
                      {typeof passedCount === "number" ? passedCount.toLocaleString() : "0"}
                    </span>
                  </div>
                  <div className="stat-row flex justify-between text-sm failed">
                    <span className="stat-label text-red-600">Failed:</span>
                    <span className="stat-value font-semibold text-red-600">
                      {typeof failedCount === "number" ? failedCount.toLocaleString() : "0"}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Radial Popup on Click */}
      {selectedPieChartDimension && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 bg-black/50"
          onClick={() => setSelectedPieChartDimension(null)}
        >
          <div
            className="relative w-96 h-96 bg-white rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 z-20 p-1 text-gray-400 hover:text-gray-600 transition-colors"
              onClick={() => setSelectedPieChartDimension(null)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Popup Content */}
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Center Circle - Dimension Name */}
              <div
                className="absolute w-24 h-24 rounded-full border-4 flex items-center justify-center font-bold text-white z-10"
                style={{
                  borderColor:
                    selectedPieChartDimension.dimension === "Completeness"
                      ? "#3b82f6"
                      : selectedPieChartDimension.dimension === "Uniqueness"
                      ? "#f97316"
                      : "#10b981",
                  backgroundColor:
                    selectedPieChartDimension.dimension === "Completeness"
                      ? "#3b82f6"
                      : selectedPieChartDimension.dimension === "Uniqueness"
                      ? "#f97316"
                      : "#10b981",
                }}
              >
                <div className="text-center text-xs px-2 py-1">
                  {selectedPieChartDimension.dimension}
                </div>
              </div>

              {/* SVG Connection Lines */}
              <svg className="absolute w-full h-full top-0 left-0" style={{ pointerEvents: "none", zIndex: 1 }}>
                {(() => {
                  const dimension = distributionData.find(
                    (d) => d.name === selectedPieChartDimension.dimension
                  );
                  const columns = dimension?.columns || [];
                  const centerX = 200;
                  const centerY = 200;
                  const centerRadius = 48;
                  const outerRadius = 130;
                  const dimensionColor =
                    selectedPieChartDimension.dimension === "Completeness"
                      ? "#3b82f6"
                      : selectedPieChartDimension.dimension === "Uniqueness"
                      ? "#f97316"
                      : "#10b981";

                  return columns.map((column, colIdx) => {
                    const totalColumns = columns.length;
                    const angle = (colIdx * 360) / totalColumns - 90;
                    const startX = centerX + Math.cos((angle * Math.PI) / 180) * centerRadius;
                    const startY = centerY + Math.sin((angle * Math.PI) / 180) * centerRadius;
                    const endX = centerX + Math.cos((angle * Math.PI) / 180) * outerRadius;
                    const endY = centerY + Math.sin((angle * Math.PI) / 180) * outerRadius;

                    return (
                      <line
                        key={`line-${colIdx}`}
                        x1={startX}
                        y1={startY}
                        x2={endX}
                        y2={endY}
                        stroke={dimensionColor}
                        strokeWidth="2"
                        strokeDasharray="4,4"
                        opacity="0.6"
                      />
                    );
                  });
                })()}
              </svg>

              {/* Column Nodes */}
              {(() => {
                const dimension = distributionData.find(
                  (d) => d.name === selectedPieChartDimension.dimension
                );
                const columns = dimension?.columns || [];
                const centerX = 200;
                const centerY = 200;
                const outerRadius = 130;
                const dimensionColor =
                  selectedPieChartDimension.dimension === "Completeness"
                    ? "#3b82f6"
                    : selectedPieChartDimension.dimension === "Uniqueness"
                    ? "#f97316"
                    : "#10b981";

                return columns.map((column, colIdx) => {
                  const totalColumns = columns.length;
                  const angle = (colIdx * 360) / totalColumns - 90;
                  const x = centerX + Math.cos((angle * Math.PI) / 180) * outerRadius;
                  const y = centerY + Math.sin((angle * Math.PI) / 180) * outerRadius;

                  return (
                    <div
                      key={`col-${colIdx}`}
                      className="absolute w-16 h-16 rounded-full border-2 flex items-center justify-center bg-white font-semibold text-xs text-center p-2 shadow-md hover:shadow-lg transition-all cursor-pointer"
                      style={{
                        left: `${x}px`,
                        top: `${y}px`,
                        transform: "translate(-50%, -50%)",
                        borderColor: dimensionColor,
                        color: dimensionColor,
                        zIndex: 5,
                        animation: `popIn 0.4s ease-out ${colIdx * 0.08}s both`,
                      }}
                    >
                      {column}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DimensionDistribution;